import { buildConnectorParametersDecryptUrl } from '../../utils/connectorParametersApi';

// Get token from localStorage
const token = localStorage.getItem('token');

// Prevent duplicate token exchange (React Strict Mode double mount / callback re-entry)
let isProcessingAutodeskTokenExchange = false;

export const getAutodeskCredentialsAndCreateToken = async (connectorId, mcId, masterConnectors) => {
  try {
    const norm = (s) =>
      (s || '')
        .toString()
        .trim()
        .toUpperCase()
        .replace(/_/g, ' ')
        .replace(/\s+/g, ' ');

    // Find the Autodesk master connector details
    const autodeskConnector = masterConnectors.find(mc => 
      (mc.mcRecId?.toString() === mcId?.toString() || 
       mc.mcId?.toString() === mcId?.toString()) &&
      (() => {
        const nameNorm = norm(mc.mcName);
        const typeNorm = norm(mc.mcConnectorType);
        const source = typeNorm || nameNorm;
        return source.includes('AUTODESK') &&
          !source.includes('DOCS') &&
          !source.includes('VAULT');
      })()
    );
    if (!autodeskConnector) {
      throw new Error('Autodesk master connector not found');
    }

    const paramResponse = await fetch(buildConnectorParametersDecryptUrl(connectorId), {
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      }
    });
    if (!paramResponse.ok) {
      throw new Error('Failed to fetch connector parameters');
    }
    const connectorParams = await paramResponse.json();
    const clientId = connectorParams.find((param) => param.scpName === 'clientid')?.scpValue;
    if (!clientId) {
      throw new Error('Client ID not found in connector parameters');
    }

    // Use Master Connector auth URL
    const authEndpoint = autodeskConnector.mcAuthurl;
    if (!authEndpoint) {
      throw new Error('Autodesk Authentication URL is missing from Master Connector data');
    }
    const autodeskAuthUrl = `${authEndpoint}${authEndpoint.includes('?') ? '&' : '?'}response_type=code&client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(process.env.REACT_APP_OAUTH_REDIRECT_URI)}&state=${encodeURIComponent(connectorId)}&scope=${encodeURIComponent('account:read data:read account:write data:write')}`;

    // Store the connector ID for after redirect
    localStorage.setItem('PendingAutodeskConnectorId', connectorId);
    // Check if we already have a stored authorization code
    const storedAuthCode = localStorage.getItem('AuthCode');

    if (storedAuthCode) {
      const tokenResponse = await exchangeAutodeskAuthCodeForToken(storedAuthCode, connectorId);

      // Duplicate call returned null; keep AuthCode for the in-flight exchange
      if (tokenResponse == null) {
        return null;
      }

      localStorage.removeItem('AuthCode');
      localStorage.removeItem('PendingAutodeskConnectorId');

      return tokenResponse;
    } else {
      // Redirect to Autodesk authentication
      window.location.href = autodeskAuthUrl;
      // Return null since we're redirecting
      return null;
    }
  } catch (error) {
    console.error('Error getting Autodesk credentials:', error);
    throw error;
  }
};

// Helper function to exchange authorization code for Autodesk token
export const exchangeAutodeskAuthCodeForToken = async (authCode, mcId) => {
  if (isProcessingAutodeskTokenExchange) {
    console.warn('Autodesk token exchange already in progress; skipping duplicate to avoid auth code reuse / invalid_grant');
    return null;
  }

  isProcessingAutodeskTokenExchange = true;

  try {
    const tokenUrl = `${process.env.REACT_APP_BACKEND_URL}synctrack/autodesk/fullTokens?mcId=${mcId}`;
    
    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json'
      },
      body: new URLSearchParams({
        code: authCode,
        grant_type: 'authorization_code',
        redirect_uri: process.env.REACT_APP_OAUTH_REDIRECT_URI
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Token exchange error response:', errorText);
      throw new Error(`Failed to exchange authorization code for token: ${response.status}`);
    }

    const tokenData = await response.json();
    
    // Use the connector ID passed as parameter (mcId is actually the connectorId/ctdId)
    // This ensures we use the correct connector ID even if localStorage has a stale value
    const connectorId = mcId;
    
    if (connectorId) {
      // Update the connector with token data
      const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/sync-connectors`;
      
      try {
        // First, get the current connector data
        const connectorResponse = await fetch(`${API_BASE_URL}/${connectorId}`, {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });
        if (!connectorResponse.ok) {
          throw new Error(`Failed to fetch connector data: ${connectorResponse.status}`);
        }
        const connectorData = await connectorResponse.json();
        
        // Calculate token expiry dates
        const primaryExpiresIn = parseInt(tokenData.primary_token?.expires_in) || 3600;
        const secondaryExpiresIn = parseInt(tokenData.secondary_token?.expires_in) || 3600;
        
        // Calculate the 14-day expiry date for refresh token
        const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;
        const refreshTokenExpiry = new Date(Date.now() + FOURTEEN_DAYS_MS).toISOString();
        
        // Prepare updated connector data
        const updatedConnectorData = {
          ...connectorData,
          token: tokenData.primary_token?.access_token,
          refreshToken: tokenData.primary_token?.refresh_token,
          tokenExpiry: new Date(Date.now() + (primaryExpiresIn * 1000)).toISOString(),
          secondaryToken: tokenData.secondary_token?.access_token,
          secondaryTokenExpiry: new Date(Date.now() + (secondaryExpiresIn * 1000)).toISOString(),
          refreshUrl: connectorData.refreshUrl,
          refreshTokenExpiry: refreshTokenExpiry,
          status: 'Connected',
          connectedTime: new Date().toISOString()
        };
        
        // Make the PUT request to update the connector
        const updateResponse = await fetch(`${API_BASE_URL}/${connectorId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(updatedConnectorData)
        });
        
        if (!updateResponse.ok) {
          const errorText = await updateResponse.text();
          console.error('Update connector error:', errorText);
          throw new Error(`Failed to update connector with tokens: ${updateResponse.status}`);
        }
        
        console.log('Connector updated successfully with tokens');
        // Do not reload: reload can re-run OAuth callback and exchange the same code twice (invalid_grant).
      } catch (error) {
        console.error('Error updating connector with tokens:', error);
      }
    }
    return tokenData;
  } catch (error) {
    console.error('Token exchange error:', error);
    throw error;
  } finally {
    isProcessingAutodeskTokenExchange = false;
  }
};


